/**
 * Add some enum definitions to ensure correct values are used for
 * date format options
 */
enum NumericOptions {
  Numeric = "numeric",
  TwoDigit = "2-digit"
}

enum StringOptions {
  Narrow = "narrow",
  Short = "short",
  Long = "long"
}

enum MonthOptions {
  Numeric = "numeric",
  TwoDigit = "2-digit",
  Narrow = "narrow",
  Short = "short",
  Long = "long"
}

// This is currently unused:
enum TimeZoneOptions {
  Short = "short",
  Long = "long"
}

/**
 * Formats are as follows (all examples en-US):
 *
 * shortFullDate - Dec 6, 2017
 *
 * longFullDate - December 6, 2017
 *
 * shortFullDateTime - Dec 6, 2017 2:14:37 PM
 *
 * shortFullDateTimeNoSeconds - Dec 6, 2017 2:14 PM
 *
 * longFullMonthYear - December 2017
 *
 * longFullDateTime - December 6, 2017 2:14:37 PM
 *
 * longMonthAndDay - December 6
 *
 * shortMonthAndDay - Dec 06
 *
 * shortMonthAndShortDate - Dec 6
 *
 * shortTime - 2:14 PM
 *
 * shortMonthAndDayAndTime - Dec 6, 2:14 PM
 *
 * longFullDdateTimeWithWeekday - Wednesday December 6, 2017 2:14 PM
 *
 * full24HourTime - 14:14:37
 *
 * longFullDateTimeWithSecondsAndWeekday - Wednesday December 6, 2017 2:14:37 PM
 */
export type DateFormats =
  | "shortFullDate"
  | "longFullDate"
  | "shortFullDateTime"
  | "shortFullDateTimeNoSeconds"
  | "longFullMonthYear"
  | "longFullDateTime"
  | "longMonthAndDay"
  | "longMonthAndDayWithTimezone"
  | "shortMonthAndDay"
  | "shortMonthAndShortDate"
  | "shortTime"
  | "shortMonthAndDayAndTime"
  | "longFullDateTimeWithWeekday"
  | "full24HourTime"
  | "longFullDateTimeWithSecondsAndWeekday";

const DateFormat: { [F in DateFormats]: Intl.DateTimeFormatOptions } = {
  shortFullDate: {
    month: MonthOptions.Short,
    year: NumericOptions.Numeric,
    day: NumericOptions.Numeric
  },
  longFullDate: {
    month: MonthOptions.Long,
    year: NumericOptions.Numeric,
    day: NumericOptions.Numeric
  },
  shortFullDateTime: {
    month: MonthOptions.Short,
    year: NumericOptions.Numeric,
    day: NumericOptions.Numeric,
    hour: NumericOptions.Numeric,
    minute: NumericOptions.Numeric,
    second: NumericOptions.Numeric
  },
  shortFullDateTimeNoSeconds: {
    month: MonthOptions.Short,
    year: NumericOptions.Numeric,
    day: NumericOptions.Numeric,
    hour: NumericOptions.Numeric,
    minute: NumericOptions.Numeric
  },
  longFullMonthYear: {
    month: MonthOptions.Long,
    year: NumericOptions.Numeric
  },
  longFullDateTime: {
    month: MonthOptions.Long,
    year: NumericOptions.Numeric,
    day: NumericOptions.Numeric,
    hour: NumericOptions.Numeric,
    minute: NumericOptions.Numeric,
    second: NumericOptions.Numeric
  },
  longMonthAndDay: {
    month: MonthOptions.Long,
    day: NumericOptions.Numeric
  },
  longMonthAndDayWithTimezone: {
    month: MonthOptions.Long,
    day: NumericOptions.Numeric,
    timeZoneName: TimeZoneOptions.Short
  },
  shortMonthAndDay: {
    month: MonthOptions.Short,
    day: NumericOptions.TwoDigit
  },
  shortMonthAndShortDate: {
    month: MonthOptions.Short,
    day: NumericOptions.Numeric
  },
  shortTime: {
    hour: NumericOptions.Numeric,
    minute: NumericOptions.Numeric
  },
  shortMonthAndDayAndTime: {
    month: MonthOptions.Short,
    day: NumericOptions.Numeric,
    hour: NumericOptions.Numeric,
    minute: NumericOptions.Numeric
  },
  longFullDateTimeWithWeekday: {
    weekday: StringOptions.Long,
    month: MonthOptions.Long,
    year: NumericOptions.Numeric,
    day: NumericOptions.Numeric,
    hour: NumericOptions.Numeric,
    minute: NumericOptions.Numeric
  },
  full24HourTime: {
    hour: NumericOptions.TwoDigit,
    minute: NumericOptions.TwoDigit,
    second: NumericOptions.TwoDigit,
    hour12: false
  },
  longFullDateTimeWithSecondsAndWeekday: {
    weekday: StringOptions.Long,
    month: MonthOptions.Long,
    day: NumericOptions.Numeric,
    year: NumericOptions.Numeric,
    hour: NumericOptions.Numeric,
    minute: NumericOptions.Numeric,
    second: NumericOptions.Numeric
  }
};

/**
 * Checks whether the supplied date format name is supported by the formatter function.
 *
 * @export
 * @param {string} format The name of the date format to check.
 * @returns {format is DateFormats}
 */
export function isDateFormat(format?: string): format is DateFormats {
  if (!format) {
    return false;
  }

  const parts = format.split(":");
  return DateFormat.hasOwnProperty(parts[0]);
}

function getFormatOptions(format: DateFormats | string): Intl.DateTimeFormatOptions {
  const parts = format.split(":");
  const options: Intl.DateTimeFormatOptions = DateFormat[parts[0]];

  if (parts.length === 2 && parts[1] === "utc") {
    return { ...options, timeZone: "UTC" };
  }

  return { ...options };
}
/**
 * Formats a date value with the named format options, according to the localization rules of the language.
 *
 * @export
 * @param {Date} date The date value to format.
 * @param {DateFormats} format The named date format.
 * @param {string} language The language code whose localization rules should be used to format the date value.
 * @returns {string} The formatted localized date value.
 */
export function formatLocalizedDate(date: Date, format: DateFormats | string, language?: string): string {
  if (!global.Intl) {
    // We can't localize the date for users whose browsers don't support Intl, so at least return something.
    return date.toString();
  }

  // More information about formatting in i18next can be found here:
  // https://www.i18next.com/formatting.html
  const options = getFormatOptions(format);
  const formatter = new Intl.DateTimeFormat(language, options);

  return formatter.format(date);
}
